/**
 * Pure edit logic for the `/config/:slug` staff-allowlist editor (2026-08-06).
 *
 * WHY THIS MODULE EXISTS — a real production incident. The editor keeps the row
 * list in React state; `update()` writes EVERY KEYSTROKE of the phone field into
 * it, while the admin checkbox instant-saves. Instant-saving sent the WHOLE
 * array, so ticking one row's admin box committed another row's half-typed
 * phone. The save path is a full-list `DELETE` + re-`INSERT` and writes no
 * `audit_log` row, so the damage was both immediate and untraceable.
 *
 * On 2026-08-06 that produced three corrupted variants of the operator's number
 * in one day (`…5279`, `…5271`, `…5275`). Each passed the server's E.164 regex —
 * they are well-formed numbers, just not anyone's — so validation could not
 * catch it, and every staff alert to that row silently bounced Meta 131026 until
 * someone noticed.
 *
 * The fix is to make the editor honour the contract its own comment already
 * claimed: "other fields still persist via Save allowlist". An admin toggle
 * saves the admin bit against the LAST SERVER-CONFIRMED list; in-progress text
 * stays local until the operator explicitly saves it.
 *
 * Pure + separately tested because `apps/dashboard/web/**` is ESLint-ignored and
 * excluded from coverage — the same reason `pr-person-edit.ts` and
 * `reply-channels.ts` exist.
 */

export interface StaffAllowlistEntryDraft {
  phone: string;
  staff_id: string;
  display_name: string;
  /**
   * Optional to match the editor's own row type and the API response, where the
   * server may omit it. Absent is read as `false` everywhere below — never as
   * "unknown", because an unknown admin bit would be the one thing worth
   * refusing to save.
   */
  admin?: boolean;
  /**
   * Migration 160 — "Don't send me any messages". Orthogonal to `admin`: this
   * governs whether we CONTACT the person, `admin` governs what they may DO.
   * Same optional/absent-is-false convention as `admin`.
   */
  alerts_muted?: boolean;
  /**
   * 2026-08-12 — `notify_staff`'s email address and the inbound-Telegram
   * identity, now rendered by the editor. TEXT fields, so they are typed
   * character by character and belong in {@link TEXT_FIELDS} — not in
   * {@link FLAG_FIELDS}. Getting that wrong would reproduce this module's
   * founding bug in a new column: a flag toggle builds its POST from `saved`,
   * so an in-progress email edit would be silently discarded, and without the
   * field in `TEXT_FIELDS` the "you have unsaved edits" notice would not even
   * fire to say so.
   *
   * `null` = stored as unset. `undefined` = the row came from a client that did
   * not carry the field.
   */
  email?: string | null;
  telegram_chat_id?: string | null;
  /**
   * Migration 177 — the DASHBOARD LOGIN address, distinct from `email` (where
   * `notify_staff` writes). A TEXT field: it is typed character by character,
   * so it belongs in {@link TEXT_FIELDS} with the other two and NOT in
   * {@link FLAG_FIELDS}. Getting that wrong is this module's founding bug in
   * its most expensive column yet — a flag toggle builds its POST from
   * `saved`, so a half-typed login address would be silently discarded, and
   * the "you have unsaved edits" notice would not even fire to say so.
   *
   * `null` = stored as unset. `undefined` = the row came from a client that
   * did not carry the field.
   */
  login_email?: string | null;
  /**
   * Migration 173 — the voter grant (WHO may vote on staff decisions; the
   * grid's `decisions` row is WHERE the ballot reaches them). A flag like
   * `admin` / `alerts_muted`: instant-save checkbox, submitted-entry
   * semantics on the server's full replace, default FALSE.
   */
  voter?: boolean;
  /**
   * Migration 192 — the per-person notification grid
   * `{ "<type>": ["whatsapp"|"telegram"|"email", ...] }`, replacing the 17
   * `notify_*` boolean rails. The THIRD field class of this module:
   * instant-save like a flag (each tick in the three channel sections POSTs
   * at once), but STRUCTURED — so it has its own toggle builder
   * ({@link buildGridToggleList}) instead of riding {@link FLAG_FIELDS},
   * and {@link mergeFlags} folds it back from the server response like the
   * flags it sits beside. `undefined` = the row came from a client that did
   * not carry the field; the POST then OMITS the key, which the server
   * reads as "keep the stored grid" — the stale-tab guard. An absent TYPE
   * inside the grid is OFF, and empty arrays are never stored (a type with
   * no channels loses its key).
   */
  notification_grid?: Readonly<Record<string, ReadonlyArray<string>>>;
}

/** The text fields an operator edits by typing — the ones an instant-save must not touch. */
const TEXT_FIELDS = [
  "phone",
  "staff_id",
  "display_name",
  "email",
  "telegram_chat_id",
  "login_email",
] as const;

/**
 * The boolean fields that instant-save on click. Enumerated ONCE so a new flag
 * cannot be added to the row type and then silently dropped by a save: every
 * function below folds over this list rather than naming `admin` by hand.
 */
const FLAG_FIELDS = ["admin", "alerts_muted", "voter"] as const;

export type StaffAllowlistFlagField = (typeof FLAG_FIELDS)[number];

/**
 * What an ABSENT flag means — the server column's default. All three
 * remaining flags default FALSE (the 17 default-true `notify_*` rails left
 * with migration 192), but the lookup stays: the full-replace save writes an
 * omitted flag as its column default, so the editor must read absence the
 * same way or its normalised POST changes state.
 */
const FLAG_DEFAULTS: Record<StaffAllowlistFlagField, boolean> = {
  admin: false,
  alerts_muted: false,
  voter: false,
};

/** Every flag normalised to a real boolean — absent reads as its column default. */
function withNormalisedFlags(
  e: StaffAllowlistEntryDraft,
): StaffAllowlistEntryDraft {
  const out = { ...e };
  for (const f of FLAG_FIELDS) out[f] = e[f] ?? FLAG_DEFAULTS[f];
  return out;
}

/**
 * The list to POST for a flag-checkbox toggle (Admin, or "Don't send me any
 * messages").
 *
 * Built from `saved` (the last server-confirmed list), NOT from the in-progress
 * editor state — that is the entire fix. Only `field` at `idx` differs; every
 * OTHER flag on every row is carried across normalised, because the save is a
 * full-list replace and an omitted flag is erasure, not "leave alone".
 *
 * Falls back to `local` when the two lists have diverged structurally (a row
 * added or removed since the last save), because an index into `saved` would
 * then address a different row. Saving the wrong row's flag is worse than the
 * keystroke bug: better to persist what the operator can actually see.
 */
export function buildFlagToggleList(
  saved: ReadonlyArray<StaffAllowlistEntryDraft>,
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  idx: number,
  field: StaffAllowlistFlagField,
  checked: boolean,
): StaffAllowlistEntryDraft[] {
  const base = saved.length === local.length ? saved : local;
  return base.map((e, i) => {
    const normalised = withNormalisedFlags(e);
    return i === idx ? { ...normalised, [field]: checked } : normalised;
  });
}

/**
 * One grid cell flipped — the pure step both the POST builder below and the
 * editor's optimistic local reflect share, so the two can never disagree on
 * what a tick means. Adds/removes `channel` on `gridType`'s array and
 * DELETES the type's key when its last channel unticks: an empty array is
 * never produced, absence IS the off state.
 */
export function withGridCell(
  grid: Readonly<Record<string, ReadonlyArray<string>>> | undefined,
  gridType: string,
  channel: string,
  checked: boolean,
): Record<string, ReadonlyArray<string>> {
  const current = grid ?? {};
  const currentChannels = current[gridType] ?? [];
  const nextChannels = checked
    ? currentChannels.includes(channel)
      ? currentChannels
      : [...currentChannels, channel]
    : currentChannels.filter((c) => c !== channel);
  const next: Record<string, ReadonlyArray<string>> = { ...current };
  if (nextChannels.length === 0) {
    delete next[gridType];
  } else {
    next[gridType] = nextChannels;
  }
  return next;
}

/**
 * The list to POST for a notification-grid tick (one channel of one type on
 * one row, from the three channel sections).
 *
 * Same anti-keystroke-bug construction as {@link buildFlagToggleList}: built
 * from `saved`, only the target cell differs ({@link withGridCell}), `local`
 * fallback on structural divergence. Every other row's grid rides through
 * verbatim, and a row whose grid the client never carried keeps OMITTING the
 * key (server-side carry-over) rather than having one manufactured for it —
 * except the toggled row itself, whose single tick necessarily materialises
 * a grid to carry it.
 */
export function buildGridToggleList(
  saved: ReadonlyArray<StaffAllowlistEntryDraft>,
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  idx: number,
  gridType: string,
  channel: string,
  checked: boolean,
): StaffAllowlistEntryDraft[] {
  const base = saved.length === local.length ? saved : local;
  return base.map((e, i) => {
    const normalised = withNormalisedFlags(e);
    if (i !== idx) return normalised;
    return {
      ...normalised,
      notification_grid: withGridCell(
        e.notification_grid,
        gridType,
        channel,
        checked,
      ),
    };
  });
}

/**
 * Fold a server response back into the editor state after a flag or grid
 * save.
 *
 * Takes the server's flags AND notification grid (authoritative — that is
 * what was just saved) while KEEPING local text edits, which the server never
 * saw. A plain `setEntries(resp.entries)` would discard whatever the operator
 * was typing, which is how a half-typed value got committed in the first
 * place: the operator retyped it, and the retype raced the next save.
 *
 * Length mismatch ⇒ the server list wins wholesale; there is no safe row-wise
 * correspondence to preserve.
 */
export function mergeFlags(
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  serverEntries: ReadonlyArray<StaffAllowlistEntryDraft>,
): StaffAllowlistEntryDraft[] {
  if (local.length !== serverEntries.length) {
    return serverEntries.map(withNormalisedFlags);
  }
  return local.map((e, i) => {
    const server = serverEntries[i];
    const out = { ...e };
    for (const f of FLAG_FIELDS) out[f] = server?.[f] ?? FLAG_DEFAULTS[f];
    // The GET/save response always carries the grid (NOT NULL column); the
    // `?? {}` covers only a malformed response, and folding `{}` in beats
    // keeping a local guess the server may not hold.
    out.notification_grid = server?.notification_grid ?? {};
    return out;
  });
}

/**
 * True when any row's TEXT differs from the last server-confirmed list — i.e.
 * the operator has unsaved edits that a flag toggle deliberately did not
 * persist. Drives the notice that keeps the instant-save from being a silent
 * partial save. Flags are excluded: those save instantly by design.
 */
export function hasUnsavedTextEdits(
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  saved: ReadonlyArray<StaffAllowlistEntryDraft>,
): boolean {
  if (local.length !== saved.length) return true;
  return local.some((entry, i) => {
    const before = saved[i];
    if (before === undefined) return true;
    // `?? null` because an absent key and a stored NULL are the same state and
    // must not read as an edit — otherwise the "unsaved edits" notice would be
    // permanently on for every row with no email, and a notice that is always
    // on is a notice nobody reads.
    return TEXT_FIELDS.some((f) => (entry[f] ?? null) !== (before[f] ?? null));
  });
}
